import {
  BUILDING_LABELS,
  FIELD_LABELS,
  REPORT_LABELS,
  RESOURCE_LABELS,
  UNIT_LABELS,
  UNIT_TYPES,
  type FieldType,
  type ReportKind,
  type UnitType,
} from '@bienenkampf/shared';
import { Badge, Button, Card } from '@bienenkampf/ui';
import { useMutation } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatDateTime, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

const ICON: Record<ReportKind, string> = {
  RAID_ATTACK: '⚔',
  RAID_DEFENSE: '🛡',
  SCOUT: '🔭',
  SCOUT_DEFENSE: '👁',
  FIELD_COMBAT: '🌼',
  QUEEN: '👑',
  COLONIZE: '🏠',
  WINTER: '❄',
};

export function ReportsPage() {
  const { worldId: worldIdStr } = useParams({ from: '/w/$worldId' });
  const worldId = Number(worldIdStr);
  const trpc = useTRPC();
  const now = useNow(10_000);
  const [selected, setSelected] = useState<number | null>(null);
  const list = useQuery(trpc.report.list.queryOptions({ worldId, limit: 50 }));
  const search = useSearch({ from: '/w/$worldId/berichte' });

  return (
    <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[22rem_1fr] lg:items-start">
      <Card title="Berichte">
        {list.isPending && <p className="text-sm text-stone-400">Lade…</p>}
        {list.data?.items.length === 0 && (
          <p className="text-sm text-stone-400">
            Noch keine Berichte. Raubzüge und Spähflüge erzeugen hier Einträge.
          </p>
        )}
        <ul className="flex flex-col divide-y divide-stone-800">
          {list.data?.items.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setSelected(r.id)}
                className={`flex w-full items-start gap-2 py-2 text-left ${selected === r.id ? 'text-amber-300' : ''}`}
              >
                <span className="text-lg leading-none">{ICON[r.kind]}</span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-sm ${r.readAt ? 'text-stone-300' : 'font-semibold text-stone-100'}`}
                  >
                    {r.title}
                  </span>
                  <span className="block text-xs text-stone-500">
                    {REPORT_LABELS[r.kind]} · {formatAbsRel(r.createdAt, now)}
                  </span>
                </span>
                {!r.readAt && <span className="mt-1.5 size-2 rounded-full bg-amber-400" />}
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <div>
        {search.token ? (
          <SharedReport worldId={worldId} token={search.token} />
        ) : selected ? (
          <ReportDetail worldId={worldId} id={selected} />
        ) : (
          <Card>
            <p className="text-sm text-stone-400">Wähle links einen Bericht.</p>
          </Card>
        )}
      </div>
    </div>
  );
}

type Side = {
  hiveName: string;
  playerName: string;
  q: number;
  r: number;
  units?: Record<string, number>;
  losses?: Record<string, number>;
  after?: Record<string, number>;
  power?: number;
  guardCombLevel?: number;
};

function ReportDetail({ worldId, id }: { worldId: number; id: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const r = useQuery({
    ...trpc.report.get.queryOptions({ worldId, id }),
    // Nach dem Oeffnen gilt der Bericht als gelesen -> Liste und Badge nachziehen
  });
  if (r.isSuccess && r.data && !r.data.readAt) {
    void qc.invalidateQueries({ queryKey: trpc.report.list.pathKey() });
    void qc.invalidateQueries({ queryKey: trpc.report.unreadCount.pathKey() });
  }
  if (r.isPending)
    return (
      <Card>
        <p className="text-sm text-stone-400">Lade…</p>
      </Card>
    );
  if (r.isError)
    return (
      <Card>
        <p className="text-sm text-red-400">{r.error.message}</p>
      </Card>
    );
  const rep = r.data;
  const p = rep.payload as Record<string, unknown>;

  return (
    <Card title={rep.title} action={<Badge>{formatDateTime(rep.createdAt)}</Badge>}>
      {(rep.kind === 'RAID_ATTACK' || rep.kind === 'RAID_DEFENSE') && <RaidBody p={p} />}
      {rep.kind === 'SCOUT' && <ScoutBody p={p} />}
      {rep.kind === 'FIELD_COMBAT' && <FieldCombatBody p={p} />}
      {rep.kind === 'QUEEN' && <QueenBody p={p} />}
      {rep.kind === 'COLONIZE' && <ColonizeBody p={p} />}
      {rep.kind === 'WINTER' && <WinterBody p={p} />}
      {rep.kind === 'SCOUT_DEFENSE' && (
        <p className="text-sm text-stone-300">
          {String(p.scouts)} Späherinnen von {(p.attacker as Side)?.playerName} wurden bei{' '}
          {(p.target as Side)?.hiveName} abgefangen.
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <ShareToChat worldId={worldId} reportId={rep.id} title={rep.title} kind={rep.kind} />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigator.clipboard?.writeText(JSON.stringify(rep.payload, null, 2))}
        >
          Rohdaten kopieren
        </Button>
      </div>
    </Card>
  );
}

function RaidBody({ p }: { p: Record<string, unknown> }) {
  if (p.result === 'shielded') {
    return (
      <p className="text-sm text-stone-300">
        Der Zug drehte ab: Der Zielstock hatte bei der Ankunft ein Schutzschild.
      </p>
    );
  }
  const a = p.attacker as Side;
  const d = p.defender as Side;
  const loot = p.loot as { nectar: number; pollen: number; wax: number } | undefined;
  const win = p.result === 'attacker';
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={win ? 'green' : 'red'}>{win ? 'Angreifer gewinnt' : 'Verteidiger hält'}</Badge>
        <span className="text-stone-400 tabular-nums">
          A {formatNumber(a.power ?? 0)} : D {formatNumber(d.power ?? 0)} · r ={' '}
          {p.ratio === null ? '∞' : String(p.ratio)} ·{' '}
          {String(p.season) === 'WINTER' ? 'Winter' : 'Tracht'} · Wächterwabe{' '}
          {d.guardCombLevel ?? 0}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SideTable title={`${a.playerName} · ${a.hiveName} [${a.q}|${a.r}]`} side={a} />
        <SideTable title={`${d.playerName} · ${d.hiveName} [${d.q}|${d.r}]`} side={d} />
      </div>
      {loot &&
        (loot.nectar + loot.pollen + loot.wax > 0 ? (
          <p className="text-amber-300">
            Beute:{' '}
            {(['nectar', 'pollen', 'wax'] as const)
              .filter((k) => loot[k] > 0)
              .map((k) => `${formatNumber(loot[k])} ${RESOURCE_LABELS[k]}`)
              .join(', ')}
          </p>
        ) : (
          <p className="text-stone-400">Keine Beute.</p>
        ))}
      {(() => {
        const qo = p.queen as
          | {
              chance: number;
              killed: string | null;
              successor: string | null;
              queenlessUntil: string | null;
            }
          | undefined;
        if (!qo) return null;
        if (qo.killed)
          return (
            <p className="text-red-300">
              👑 Königin {qo.killed} ist gefallen (Chance {Math.round(qo.chance * 100)} %).{' '}
              {qo.successor
                ? `Reservekönigin ${qo.successor} regiert nun.`
                : qo.queenlessUntil
                  ? `Der Stock ist weisellos bis ${formatDateTime(new Date(qo.queenlessUntil))}.`
                  : ''}
            </p>
          );
        if (win)
          return (
            <p className="text-stone-400">
              Königin überlebt (Chance {Math.round(qo.chance * 100)} %).
            </p>
          );
        return null;
      })()}
      {p.shieldUntil ? (
        <p className="text-sky-300">
          Der Stock erhielt ein Schutzschild bis {formatDateTime(new Date(String(p.shieldUntil)))}.
        </p>
      ) : null}
    </div>
  );
}

type FieldSide = {
  playerId: number;
  playerName: string;
  allianceTag: string | null;
  units?: Record<string, number>;
  losses?: Record<string, number>;
  after?: Record<string, number>;
  power?: number;
};

function FieldCombatBody({ p }: { p: Record<string, unknown> }) {
  const a = p.attacker as FieldSide & { hiveName: string };
  const ds = (p.defenders as FieldSide[] | undefined) ?? [];
  const f = p.field as { q: number; r: number; type: string; resource: string } | undefined;
  const win = p.result === 'attacker';
  const tag = (s: FieldSide) => (s.allianceTag ? `[${s.allianceTag}] ` : '');
  const where = f ? `${FIELD_LABELS[f.type as FieldType] ?? f.type} [${f.q}|${f.r}]` : '';
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={win ? 'green' : 'red'}>
          {win ? 'Ankommende gewinnen' : 'Besetzer halten das Feld'}
        </Badge>
        <span className="text-stone-400 tabular-nums">
          {where} · A {formatNumber(a.power ?? 0)} : D {formatNumber(Number(p.defenderPower ?? 0))}{' '}
          · r = {p.ratio === null ? '∞' : String(p.ratio)} ·{' '}
          {String(p.season) === 'WINTER' ? 'Winter' : 'Tracht'} · offenes Feld
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SideTable
          title={`Ankommend: ${tag(a)}${a.playerName} · ${a.hiveName}`}
          side={a as unknown as Side}
        />
        {ds.map((d, i) => (
          <SideTable
            key={`${d.playerId}-${i}`}
            title={`Am Feld: ${tag(d)}${d.playerName}`}
            side={d as unknown as Side}
          />
        ))}
      </div>
      <p className="text-xs text-stone-500">
        Feldkampf: keine Wächterwabe, kein Heimvorteil. Verluste treffen jede Partei anteilig; wer
        alles verliert, ist vom Feld.
      </p>
    </div>
  );
}

function QueenBody({ p }: { p: Record<string, unknown> }) {
  const q = p.queen as { name: string } | undefined;
  const ev = String(p.event);
  const losses = (p.losses as Record<string, number> | undefined) ?? {};
  const lossText = Object.entries(losses)
    .filter(([, n]) => n > 0)
    .map(([t, n]) => `${formatNumber(n)} ${UNIT_LABELS[t as UnitType]}`)
    .join(', ');
  const text: Record<string, string> = {
    hatched_ruling: `${q?.name} ist geschlüpft und hat den weisellosen Stock sofort übernommen.`,
    hatched_reserve: `${q?.name} ist geschlüpft und wartet als Reserve in der Weiselzelle.`,
    emergency: `Die Frist der Weisellosigkeit lief ab. Notkönigin ${q?.name} regiert nun; verloren: ${lossText || 'keine Bienen'}.`,
    delivered_ruling: `${q?.name} wurde von ${String(p.fromPlayerName)} an ${String(p.toPlayerName)} geliefert und regiert jetzt ${String(p.targetHiveName)}.`,
    delivered_reserve: `${q?.name} wurde von ${String(p.fromPlayerName)} an ${String(p.toPlayerName)} geliefert und steht in ${String(p.targetHiveName)} als Reserve.`,
    delivery_rejected:
      p.reason === 'not_allied'
        ? `${q?.name} kehrt um: ${String(p.targetHiveName)} gehört keinem Verbündeten mehr.`
        : `${q?.name} kehrt um: In ${String(p.targetHiveName)} ist kein Platz (Weiselzelle voll).`,
  };
  return <p className="text-sm text-stone-300">{text[ev] ?? ev}</p>;
}

function ColonizeBody({ p }: { p: Record<string, unknown> }) {
  const ev = String(p.event);
  const t = p.target as
    { kind: string; name?: string; q: number; r: number; points?: number } | undefined;
  const where = t ? (t.kind === 'hive' ? `${t.name} [${t.q}|${t.r}]` : `[${t.q}|${t.r}]`) : '';
  const units = (p.units as Record<string, number> | undefined) ?? {};
  const losses = (p.losses as Record<string, number> | undefined) ?? {};
  const after = (p.survivors as Record<string, number> | undefined) ?? {};
  const respawn = p.respawn as { name: string; q: number; r: number } | null | undefined;
  const nh = p.newHive as { name: string; q: number; r: number } | undefined;
  if (ev === 'aborted')
    return (
      <p className="text-sm text-stone-300">
        Der Zug drehte ab (${String(p.reason)}). Königin {String(p.queen)} und Hofstaat kehren
        zurück.
      </p>
    );
  const tone = ev === 'taken' || ev === 'founded' || ev === 'defended' ? 'green' : 'red';
  const label: Record<string, string> = {
    taken: 'Übernommen',
    founded: 'Kolonie gegründet',
    failed: 'Gescheitert – Königin gefallen',
    defended: 'Übernahme abgewehrt',
    lost: 'Stock verloren',
  };
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tone}>{label[ev] ?? ev}</Badge>
        <span className="text-stone-400 tabular-nums">
          {where} · Verlust {Math.round(Number(p.lossRate ?? 0) * 100)} % · angekommen{' '}
          {formatNumber(Number(p.survivorCount ?? 0))} / nötig{' '}
          {formatNumber(Number(p.required ?? 0))} ·{' '}
          {String(p.season) === 'WINTER' ? 'Winter' : 'Tracht'}
          {Number(p.remainingPop ?? 0) > 0 &&
            ` · Restbevölkerung ${formatNumber(Number(p.remainingPop))}`}
        </span>
      </div>
      <SideTable
        title={`Hofstaat von ${String(p.queen)}`}
        side={{ hiveName: '', playerName: '', q: 0, r: 0, units, losses, after }}
      />
      {nh && (
        <p className="text-amber-300">
          {String(p.queen)} regiert nun {nh.name} [{nh.q}|{nh.r}].
        </p>
      )}
      {ev === 'lost' && (
        <p className="text-stone-300">
          {String(p.attackerName)} hat den Stock übernommen.{' '}
          {respawn
            ? `Du hast einen Neuanfang bei ${respawn.name} [${respawn.q}|${respawn.r}] mit Imkerschutz erhalten.`
            : 'Deine übrigen Stöcke bleiben dir.'}
        </p>
      )}
    </div>
  );
}

function WinterBody({ p }: { p: Record<string, unknown> }) {
  const ev = String(p.event);
  const lost = (p.lost as Record<string, number> | undefined) ?? {};
  const lostText = Object.entries(lost)
    .filter(([, n]) => n > 0)
    .map(([t, n]) => `${formatNumber(n)} ${UNIT_LABELS[t as UnitType]}`)
    .join(', ');
  const b = p.building as { type: string; from: number; to: number } | null | undefined;
  if (ev === 'recovery')
    return (
      <p className="text-sm text-stone-300">
        Der Winter ist vorbei. Von {formatNumber(Number(p.lost ?? 0))} verhungerten Bienen kehren{' '}
        {formatNumber(Number(p.recovered ?? 0))} als Sammlerinnen zurück.
      </p>
    );
  return (
    <div className="flex flex-col gap-2 text-sm text-stone-300">
      <p>
        Der Vorrat in {String(p.hiveName)} ist leer. Pro Stunde verhungern{' '}
        {Math.round(Number(p.decayPct ?? 0) * 100)} % der Bienen, nie unter{' '}
        {formatNumber(Number(p.minPopulation ?? 0))}.{lostText && ` Diese Stunde: ${lostText}.`}
      </p>
      {b && (
        <p className="text-red-300">
          {BUILDING_LABELS[b.type as keyof typeof BUILDING_LABELS] ?? b.type} verfällt von Stufe{' '}
          {b.from} auf {b.to}.
        </p>
      )}
      <p className="text-xs text-stone-500">
        Nektar oder Pollen einlagern (Sammelflüge, Raubzüge) stoppt den Schwund sofort.
      </p>
    </div>
  );
}

function SideTable({ title, side }: { title: string; side: Side }) {
  const rows = UNIT_TYPES.filter((t) => (side.units?.[t] ?? 0) > 0);
  return (
    <div className="rounded-lg bg-stone-900 p-3">
      <div className="mb-1 text-xs text-stone-400">{title}</div>
      {rows.length === 0 && <div className="text-xs text-stone-500">keine Bienen</div>}
      <table className="w-full text-xs tabular-nums">
        <tbody>
          {rows.map((t) => (
            <tr key={t}>
              <td className="py-0.5">{UNIT_LABELS[t as UnitType]}</td>
              <td className="text-right">{formatNumber(side.units?.[t] ?? 0)}</td>
              <td className="text-right text-red-400">−{formatNumber(side.losses?.[t] ?? 0)}</td>
              <td className="text-right">{formatNumber(side.after?.[t] ?? 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScoutBody({ p }: { p: Record<string, unknown> }) {
  const t = p.target as Side & {
    resources?: Record<string, number>;
    buildings?: Record<string, number>;
    buildQueue?: number;
    trainQueue?: number;
  };
  if (!p.success)
    return (
      <p className="text-sm text-stone-300">
        {String(p.scouts)} Späherinnen wurden bei {t.hiveName} abgefangen. Keine Informationen.
      </p>
    );
  return (
    <div className="grid gap-3 text-sm sm:grid-cols-3">
      <div className="rounded-lg bg-stone-900 p-3">
        <div className="mb-1 text-xs text-stone-400">Lager</div>
        {(['nectar', 'pollen', 'wax'] as const).map((k) => (
          <div key={k} className="flex justify-between tabular-nums">
            <span>{RESOURCE_LABELS[k]}</span>
            <span>{formatNumber(t.resources?.[k] ?? 0)}</span>
          </div>
        ))}
      </div>
      <div className="rounded-lg bg-stone-900 p-3">
        <div className="mb-1 text-xs text-stone-400">Bienen zu Hause</div>
        {UNIT_TYPES.filter((u) => (t.units?.[u] ?? 0) > 0).map((u) => (
          <div key={u} className="flex justify-between tabular-nums">
            <span>{UNIT_LABELS[u]}</span>
            <span>{formatNumber(t.units?.[u] ?? 0)}</span>
          </div>
        ))}
      </div>
      <div className="rounded-lg bg-stone-900 p-3">
        <div className="mb-1 text-xs text-stone-400">Waben</div>
        {Object.entries(t.buildings ?? {})
          .filter(([, l]) => l > 0)
          .map(([b, l]) => (
            <div key={b} className="flex justify-between tabular-nums">
              <span>{BUILDING_LABELS[b as keyof typeof BUILDING_LABELS] ?? b}</span>
              <span>{l}</span>
            </div>
          ))}
        <div className="mt-1 text-xs text-stone-500">
          Bau-Queue {t.buildQueue ?? 0} · Ausbildung {t.trainQueue ?? 0}
        </div>
      </div>
    </div>
  );
}

/** Bericht mit einem Klick in ein Gespraech schicken (Konzept 9: haeufigste Aktion im Genre). */
function ShareToChat({
  worldId,
  reportId,
  title,
  kind,
}: {
  worldId: number;
  reportId: number;
  title: string;
  kind: ReportKind;
}) {
  const trpc = useTRPC();
  const convs = useQuery(trpc.chat.conversations.queryOptions({ worldId }));
  const [target, setTarget] = useState<number | ''>('');
  const share = useMutation(trpc.report.share.mutationOptions());
  const send = useMutation(trpc.chat.send.mutationOptions());
  const busy = share.isPending || send.isPending;
  const go = async () => {
    if (target === '') return;
    const { token } = await share.mutateAsync({ worldId, id: reportId });
    await send.mutateAsync({
      worldId,
      conversationId: Number(target),
      body: '',
      attachments: [{ type: 'report', token, title, kind }],
    });
  };
  if (!convs.data || convs.data.length === 0) return null;
  return (
    <span className="flex items-center gap-1">
      <select
        className="rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-xs"
        value={target}
        onChange={(e) => setTarget(e.target.value === '' ? '' : Number(e.target.value))}
      >
        <option value="">Teilen in…</option>
        {convs.data.map((c) => (
          <option key={c.id} value={c.id}>
            {c.title}
          </option>
        ))}
      </select>
      <Button size="sm" disabled={target === '' || busy} loading={busy} onClick={() => void go()}>
        {send.isSuccess ? 'Geteilt ✓' : 'Teilen'}
      </Button>
    </span>
  );
}

/** Ueber einen Teil-Link geoeffneter Bericht eines anderen Spielers. */
function SharedReport({ worldId, token }: { worldId: number; token: string }) {
  const trpc = useTRPC();
  const r = useQuery(trpc.report.shared.queryOptions({ worldId, token }));
  if (r.isPending)
    return (
      <Card>
        <p className="text-sm text-stone-400">Lade…</p>
      </Card>
    );
  if (r.isError)
    return (
      <Card>
        <p className="text-sm text-red-400">{r.error.message}</p>
      </Card>
    );
  const rep = r.data;
  const p = rep.payload as Record<string, unknown>;
  return (
    <Card
      title={rep.title}
      action={<Badge tone="sky">geteilt · {formatDateTime(rep.createdAt)}</Badge>}
    >
      {(rep.kind === 'RAID_ATTACK' || rep.kind === 'RAID_DEFENSE') && <RaidBody p={p} />}
      {rep.kind === 'SCOUT' && <ScoutBody p={p} />}
      {rep.kind === 'FIELD_COMBAT' && <FieldCombatBody p={p} />}
      {rep.kind === 'QUEEN' && <QueenBody p={p} />}
      {rep.kind === 'COLONIZE' && <ColonizeBody p={p} />}
      {rep.kind === 'WINTER' && <WinterBody p={p} />}
    </Card>
  );
}
